import { computed, ref, shallowRef } from "vue";
import { addOrUpdateOrderPayment, getShortOrder, getFullOrder, getOrderWalletPaidAmount } from "@/core/api/graphql";
import { ProductType } from "@/core/enums";
import { groupByVendor, Logger } from "@/core/utilities";
import type {
  GetFullOrderQueryVariables,
  GetShortOrderQueryVariables,
  InputAddOrUpdateOrderPaymentType,
  CustomerOrderType,
} from "@/core/api/graphql/types";

const loading = ref(false);
const order = shallowRef<CustomerOrderType>();
const walletPaidAmount = ref<number>(0);

const giftItems = computed(() => (order.value?.items || []).filter((item) => item.isGift));
const orderItems = computed(() => (order.value?.items || []).filter((item) => !item.isGift));
const orderItemsGroupedByVendor = computed(() => groupByVendor(orderItems.value));
const allItemsAreDigital = computed(
  () => !!order.value?.items?.every((item) => item.productType === ProductType.Digital),
);
const WALLET_PAYMENT_CODE = "WalletBalance";

const shipment = computed(() => order.value?.shipments?.[0]);
const allPayments = computed(() => order.value?.inPayments ?? []);

/** Match by either gatewayCode or paymentMethod.code because the wallet payment
 * may not always have a resolved paymentMethod object. */
function isWalletPayment(p: { gatewayCode?: string | null; paymentMethod?: { code?: string | null } | null }): boolean {
  return p.gatewayCode === WALLET_PAYMENT_CODE || p.paymentMethod?.code === WALLET_PAYMENT_CODE;
}

const walletPayment = computed(() => allPayments.value.find(isWalletPayment));
/** The active payment to process — prefers the non-wallet payment when both exist. */
const payment = computed(
  () => allPayments.value.find((p) => !isWalletPayment(p)) ?? allPayments.value[0],
);
const deliveryAddress = computed(() => shipment.value?.deliveryAddress);
const pickupLocation = computed(() => shipment.value?.pickupLocation);

const billingAddress = computed(() => payment.value?.billingAddress);

export function useUserOrder() {
  async function fetchShortOrder(payload: GetShortOrderQueryVariables) {
    loading.value = true;

    try {
      order.value = (await getShortOrder(payload)) as CustomerOrderType; // todo refactor and remove assertion
    } catch (e) {
      Logger.error(`${useUserOrder.name}.${fetchShortOrder.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchFullOrder(payload: GetFullOrderQueryVariables) {
    loading.value = true;

    try {
      order.value = (await getFullOrder(payload)) as CustomerOrderType; // todo refactor and remove assertion
      const orderId = order.value?.id ?? payload.id;
      walletPaidAmount.value = orderId ? await getOrderWalletPaidAmount({ orderId }) : 0;
    } catch (e) {
      Logger.error(`${useUserOrder.name}.${fetchFullOrder.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function clearOrder() {
    order.value = undefined;
  }

  async function addOrUpdatePayment(payload: InputAddOrUpdateOrderPaymentType, reloadOrder = true) {
    loading.value = true;

    try {
      await addOrUpdateOrderPayment(payload);
    } catch (e) {
      Logger.error(`${useUserOrder.name}.${addOrUpdatePayment.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }

    if (reloadOrder) {
      await fetchFullOrder({ id: payload.orderId });
    }
  }

  return {
    loading: computed(() => loading.value),
    order: computed(() => order.value),
    walletPaidAmount: computed(() => walletPaidAmount.value),
    allItemsAreDigital,
    giftItems,
    orderItems,
    orderItemsGroupedByVendor,
    deliveryAddress,
    pickupLocation,
    billingAddress,
    shipment,
    allPayments,
    walletPayment,
    payment,
    fetchShortOrder,
    fetchFullOrder,
    clearOrder,
    addOrUpdatePayment,
  };
}
